import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import api from '../utils/api';
import { Search, Play, Calendar, Loader, FileText, Download, Eye, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PageMeta from '../components/PageMeta';

const Sermons = () => {
  const { language } = useLanguage();
  const [sermons, setSermons] = useState([]);
  const [pdfSermons, setPdfSermons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPdf, setLoadingPdf] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [activeTab, setActiveTab] = useState('videos'); // 'videos' or 'pdfs'
  const [selectedPdf, setSelectedPdf] = useState(null);

  useEffect(() => {
    fetchSermons();
    fetchPdfSermons();
  }, []);

  const fetchSermons = async (pageToken = null, query = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        max_results: '12'
      });
      
      if (query) params.append('search_query', query);
      if (pageToken) params.append('page_token', pageToken);
      
      const response = await api.get(`/sermons?${params}`);
      
      if (pageToken) {
        setSermons(prev => [...prev, ...response.data.videos]);
      } else {
        setSermons(response.data.videos);
      }
      
      setNextPageToken(response.data.nextPageToken);
    } catch (error) {
      console.error('Error fetching sermons:', error);
      // No mock data - show error message to user
      setSermons([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSermons(null, searchQuery);
  };

  const fetchPdfSermons = async () => {
    setLoadingPdf(true);
    try {
      const response = await api.get('/sermons/pdf');
      setPdfSermons(response.data.sermons_by_month || []);
      
      // Auto-expand the first month
      if (response.data.sermons_by_month && response.data.sermons_by_month.length > 0) {
        const firstMonth = `${response.data.sermons_by_month[0].year}-${response.data.sermons_by_month[0].month}`;
        setExpandedMonths({ [firstMonth]: true });
      }
    } catch (error) {
      console.error('Error fetching PDF sermons:', error);
      setPdfSermons([]);
    } finally {
      setLoadingPdf(false);
    }
  };

  const loadMore = () => {
    if (nextPageToken) {
      fetchSermons(nextPageToken, searchQuery);
    }
  };

  const toggleMonth = (year, month) => {
    const key = `${year}-${month}`;
    setExpandedMonths(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (


    <>


      <PageMeta 


        title="Sermons"


        path="/sermons"


        description="Watch and listen to our weekly sermons and teachings. Spiritual guidance in Tigrinya, Geez, and English."


      />
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="bg-gradient-to-br from-amber-100 via-orange-50 to-rose-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-6" style={{fontFamily: 'Playfair Display, serif'}}>
            {t(language, 'sermons.title')}
          </h1>
          
          {/* Tabs */}
          <div className="flex justify-center space-x-4 mb-6">
            <button
              onClick={() => setActiveTab('videos')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'videos'
                  ? 'bg-amber-600 text-white shadow-lg'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-amber-100 dark:hover:bg-gray-700'
              }`}
            >
              <Play className="w-5 h-5 inline mr-2" />
              {language === 'en' ? 'Video Sermons' : 'ቪድዮ ስብከት'}
            </button>
            <button
              onClick={() => setActiveTab('pdfs')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'pdfs'
                  ? 'bg-amber-600 text-white shadow-lg'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-amber-100 dark:hover:bg-gray-700'
              }`}
            >
              <FileText className="w-5 h-5 inline mr-2" />
              {language === 'en' ? 'PDF Sermons' : 'PDF ስብከት'}
            </button>
          </div>
          
          {/* Search - Only for videos */}
          {activeTab === 'videos' && (
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder={t(language, 'sermons.search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-4 py-6 text-lg rounded-full"
                  data-testid="sermon-search-input"
                />
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Video Player */}
      {selectedVideo && (
        <section className="bg-black py-8">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="aspect-video rounded-lg overflow-hidden shadow-2xl">
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${selectedVideo.id}`}
                title={selectedVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="video-player"
              ></iframe>
            </div>
            <div className="mt-4">
              <h2 className="text-2xl font-bold text-white mb-2">{selectedVideo.title}</h2>
              <p className="text-gray-300">{selectedVideo.description}</p>
            </div>
          </div>
        </section>
      )}

      {/* Video Sermons Grid */}
      {activeTab === 'videos' && (
        <section className="py-16 bg-white dark:bg-gray-900">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading && sermons.length === 0 ? (
              <div className="flex justify-center items-center py-20">
                <Loader className="w-8 h-8 text-amber-600 animate-spin" />
              </div>
            ) : sermons.length > 0 ? (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sermons.map((sermon) => (
                    <div
                      key={sermon.id}
                      onClick={() => setSelectedVideo(sermon)}
                      className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
                      data-testid={`sermon-card-${sermon.id}`}
                    >
                      <div className="relative aspect-video">
                        <img
                          src={sermon.thumbnail}
                          alt={sermon.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                          <Play className="w-16 h-16 text-white" />
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
                          {sermon.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                          {sermon.description}
                        </p>
                        <div className="flex items-center text-xs text-gray-500 dark:text-gray-500">
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(sermon.published_at).toLocaleDateString(language === 'en' ? 'en-US' : 'ti-ER')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {nextPageToken && (
                  <div className="text-center mt-12">
                    <Button
                      onClick={loadMore}
                      disabled={loading}
                      className="px-8 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-full"
                      data-testid="load-more-btn"
                    >
                      {loading ? (
                        <Loader className="w-5 h-5 animate-spin" />
                      ) : (
                        t(language, 'sermons.loadMore')
                      )}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <p className="text-gray-600 dark:text-gray-400 text-lg">
                  {t(language, 'sermons.noResults')}
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* PDF Sermons - Monthly/Weekly View */}
      {activeTab === 'pdfs' && (
        <section className="py-16 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loadingPdf ? (
              <div className="flex justify-center items-center py-20">
                <Loader className="w-8 h-8 text-amber-600 animate-spin" />
              </div>
            ) : pdfSermons.length > 0 ? (
              <div className="space-y-6">
                {pdfSermons.map((monthData) => {
                  const monthKey = `${monthData.year}-${monthData.month}`;
                  const isExpanded = expandedMonths[monthKey];
                  
                  return (
                    <div key={monthKey} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
                      {/* Month Header */}
                      <button
                        onClick={() => toggleMonth(monthData.year, monthData.month)}
                        className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 transition-all"
                      >
                        <div className="flex items-center space-x-3">
                          <Calendar className="w-6 h-6 text-white" />
                          <h2 className="text-2xl font-bold text-white">
                            {monthData.month_name} {monthData.year}
                          </h2>
                          <span className="bg-white text-amber-600 px-3 py-1 rounded-full text-sm font-semibold">
                            {monthData.sermons.length} {language === 'en' ? 'sermons' : 'ስብከታት'}
                          </span>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-6 h-6 text-white" />
                        ) : (
                          <ChevronDown className="w-6 h-6 text-white" />
                        )}
                      </button>

                      {/* Sermons List */}
                      {isExpanded && (
                        <div className="p-6 space-y-4">
                          {monthData.sermons.map((sermon) => (
                            <div
                              key={sermon.id}
                              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-amber-50 dark:hover:bg-gray-600 transition-all group"
                            >
                              <div className="flex items-center space-x-4 flex-1">
                                <div className="flex-shrink-0 w-16 h-16 bg-amber-100 dark:bg-amber-900 rounded-lg flex items-center justify-center">
                                  <div className="text-center">
                                    <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{sermon.day}</div>
                                    <div className="text-xs text-gray-600 dark:text-gray-400">{sermon.weekday.slice(0, 3)}</div>
                                  </div>
                                </div>
                                
                                <div className="flex-1">
                                  <h3 className="font-bold text-gray-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
                                    {sermon.title}
                                  </h3>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {sermon.weekday}, {sermon.date} • {formatFileSize(parseInt(sermon.size))}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedPdf(sermon);
                                  }}
                                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center space-x-2 transition-all"
                                >
                                  <Eye className="w-4 h-4" />
                                  <span className="hidden sm:inline">{language === 'en' ? 'View' : 'ርአይ'}</span>
                                </button>
                                <a
                                  href={sermon.download_url}
                                  download
                                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center space-x-2 transition-all"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Download className="w-4 h-4" />
                                  <span className="hidden sm:inline">{language === 'en' ? 'Download' : 'ኣውርድ'}</span>
                                </a>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400 text-lg mb-2">
                  {language === 'en' ? 'No PDF sermons available yet' : 'ገና PDF ስብከታት የለን'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  {language === 'en' 
                    ? 'PDF sermons will appear here once uploaded to Google Drive' 
                    : 'PDF ስብከታት ናብ Google Drive ምስ ዝስቀሉ ኣብዚ ክርአዩ እዮም'}
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Video Modal */}
      {selectedVideo && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4"
          onClick={() => setSelectedVideo(null)}
          data-testid="video-modal"
        >
          <div className="relative w-full max-w-6xl" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedVideo(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
              data-testid="close-video-btn"
            >
              <span className="text-4xl">×</span>
            </button>
            <div className="aspect-video">
              <iframe
                src={`https://www.youtube.com/embed/${selectedVideo.video_id}?autoplay=1`}
                title={selectedVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full rounded-lg"
              />
            </div>
            <div className="mt-4 text-white">
              <h2 className="text-2xl font-bold mb-2">{selectedVideo.title}</h2>
              <p className="text-gray-300">{selectedVideo.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {selectedPdf && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center p-4"
          onClick={() => setSelectedPdf(null)}
        >
          <div className="relative w-full h-full max-w-7xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between bg-gray-800 px-6 py-4 rounded-t-lg">
              <div className="flex-1">
                <h2 className="text-xl font-bold text-white mb-1">{selectedPdf.title}</h2>
                <p className="text-sm text-gray-400">
                  {selectedPdf.weekday}, {selectedPdf.date} • {formatFileSize(parseInt(selectedPdf.size))}
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <a
                  href={selectedPdf.download_url}
                  download
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center space-x-2 transition-all"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Download className="w-4 h-4" />
                  <span>{language === 'en' ? 'Download' : 'ኣውርድ'}</span>
                </a>
                <button
                  onClick={() => setSelectedPdf(null)}
                  className="text-white hover:text-gray-300 transition-colors p-2"
                >
                  <X className="w-8 h-8" />
                </button>
              </div>
            </div>
            
            {/* PDF Viewer */}
            <div className="flex-1 bg-gray-900 rounded-b-lg overflow-hidden">
              <iframe
                src={`https://drive.google.com/file/d/${selectedPdf.id}/preview`}
                title={selectedPdf.title}
                className="w-full h-full"
                allow="autoplay"
              />
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default Sermons;